import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, Plus, Trash2, Search } from 'lucide-react'
import { useTopicComparisonQuery, useHighYieldTopics } from '../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../stores/pyqAnalyticsStore'
import { TopicComparisonChart } from '../components/PastExamsDashboard/charts'
import { SUBJECT_COLORS } from '../constants/colors'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

// Helper to get subject color from various code formats
function getSubjectColor(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return SUBJECT_COLORS.NEET_UG_PHY
  if (code.includes('CHEM')) return SUBJECT_COLORS.NEET_UG_CHEM
  if (code.includes('BIO')) return SUBJECT_COLORS.NEET_UG_BIO
  return '#6B7280'
}

// Helper to get subject name from code
function getSubjectName(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return 'Physics'
  if (code.includes('CHEM')) return 'Chemistry'
  if (code.includes('BIO')) return 'Biology'
  return 'Unknown'
}

export default function TopicComparisonPage() {
  const navigate = useNavigate()
  const {
    comparisonTopicCodes,
    addComparisonTopic,
    removeComparisonTopic,
    clearComparisonTopics,
    yearRangeStart,
    yearRangeEnd,
  } = usePYQAnalyticsStore()

  const [searchQuery, setSearchQuery] = useState('')

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('topic_comparison', undefined, 'Topic Comparison')
  }, [])

  // Fetch comparison data
  const { data: comparisonData, isLoading: isComparisonLoading } = useTopicComparisonQuery(
    comparisonTopicCodes,
    { yearRangeStart, yearRangeEnd }
  )

  // Fetch available topics for search
  const { data: topicsData, isLoading: isTopicsLoading } = useHighYieldTopics({
    yearRangeStart,
    yearRangeEnd,
    limit: 100,
  })

  const availableTopics = topicsData?.topics.filter(
    (t) =>
      !comparisonTopicCodes.includes(t.topicCode) &&
      (searchQuery === '' ||
        t.topicName.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  // Get topics from comparison response or fall back to empty array
  const selectedTopics = comparisonData?.topics || []
  const radarChartData = comparisonData?.radarChartData || []
  const metricsData = comparisonData?.comparisonData || []

  // Calculate chart height based on number of topics
  // 4 metrics × (bar height per topic + spacing) + margins for axes/legend
  const getChartHeight = () => {
    const numTopics = radarChartData.length
    const barHeight = numTopics > 4 ? 12 : 16
    const barSpacing = 4
    const metricsCount = 4
    const groupSpacing = 20
    const axisAndLegendHeight = 80

    const rowHeight = (barHeight + barSpacing) * numTopics + groupSpacing
    const totalHeight = rowHeight * metricsCount + axisAndLegendHeight

    // Minimum height of 200px, maximum of 500px
    return Math.min(Math.max(totalHeight, 200), 500)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                Compare Topics
              </h1>
              <p className="text-sm text-gray-500">
                Select 2-10 topics to compare
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="space-y-6">
          {/* Topic Selection Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h2 className="text-sm font-medium text-gray-700 mb-4">
              Selected Topics ({comparisonTopicCodes.length}/10)
            </h2>

            {/* Selected Topics */}
            <div className="space-y-2 mb-4">
              {selectedTopics.length > 0 ? (
                selectedTopics.map((topic) => (
                  <div
                    key={topic.topicCode}
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
                        style={{
                          backgroundColor: getSubjectColor(topic.subjectCode) + '20',
                          color: getSubjectColor(topic.subjectCode),
                        }}
                      >
                        {getSubjectName(topic.subjectCode)}
                      </span>
                      <span className="font-medium text-gray-900 truncate text-sm">
                        {topic.topicName}
                      </span>
                    </div>
                    <button
                      onClick={() => removeComparisonTopic(topic.topicCode)}
                      className="p-2 text-red-500 hover:bg-red-100 rounded shrink-0 ml-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-6 bg-gray-50 rounded-lg">
                  No topics selected yet. Search and add topics below.
                </p>
              )}
            </div>

            {comparisonTopicCodes.length > 0 && (
              <button
                onClick={clearComparisonTopics}
                className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Search & Add Card */}
          {comparisonTopicCodes.length < 10 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-sm font-medium text-gray-700 mb-4">Add Topics</h2>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search topics..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-64 overflow-y-auto space-y-1">
                {isTopicsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  </div>
                ) : availableTopics?.length ? (
                  availableTopics.slice(0, 20).map((topic) => (
                    <button
                      key={topic.topicCode}
                      onClick={() => addComparisonTopic(topic.topicCode)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span
                          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
                          style={{
                            backgroundColor: getSubjectColor(topic.subjectCode) + '20',
                            color: getSubjectColor(topic.subjectCode),
                          }}
                        >
                          {getSubjectName(topic.subjectCode)}
                        </span>
                        <span className="text-sm text-gray-900 truncate">
                          {topic.topicName}
                        </span>
                      </div>
                      <Plus className="w-5 h-5 text-blue-500 shrink-0 ml-2" />
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">
                    {searchQuery ? 'No matching topics found' : 'No topics available'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Radar Chart Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h2 className="text-sm font-medium text-gray-700 mb-4">Comparison Chart</h2>
            {isComparisonLoading ? (
              <div className="h-[200px] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : radarChartData.length >= 2 ? (
              <div style={{ height: getChartHeight() }}>
                <TopicComparisonChart data={radarChartData} />
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <div className="text-center px-4">
                  <p className="text-gray-600 font-medium">Select at least 2 topics</p>
                  <p className="text-sm text-gray-500 mt-1">
                    to see the comparison chart
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Metrics Cards (Mobile-friendly) */}
          {metricsData.length > 0 && selectedTopics.length >= 2 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-sm font-medium text-gray-700 mb-4">Detailed Metrics</h2>

              {/* Mobile: Card Layout */}
              <div className="sm:hidden space-y-4">
                {metricsData.map((metric) => (
                  <div key={metric.metric} className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">{metric.metric}</h3>
                    <div className="space-y-2">
                      {selectedTopics.map((topic) => (
                        <div key={topic.topicCode} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 truncate flex-1 mr-2">
                            {topic.topicName}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {metric.values[topic.topicCode] !== undefined
                              ? metric.metric.toLowerCase().includes('frequency')
                                ? `${(metric.values[topic.topicCode] / 100).toFixed(1)}%`
                                : metric.values[topic.topicCode].toFixed(1)
                              : 'N/A'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table Layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-3 font-medium text-gray-600">Metric</th>
                      {selectedTopics.map((topic) => (
                        <th key={topic.topicCode} className="text-right py-3 px-3 font-medium text-gray-600">
                          <span className="truncate max-w-[150px] inline-block" title={topic.topicName}>
                            {topic.topicName.length > 25
                              ? topic.topicName.substring(0, 25) + '...'
                              : topic.topicName}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {metricsData.map((metric) => (
                      <tr key={metric.metric} className="border-b border-gray-100">
                        <td className="py-3 px-3 font-medium text-gray-900">
                          {metric.metric}
                        </td>
                        {selectedTopics.map((topic) => (
                          <td key={topic.topicCode} className="py-3 px-3 text-right text-gray-600">
                            {metric.values[topic.topicCode] !== undefined
                              ? metric.metric.toLowerCase().includes('frequency')
                                ? `${(metric.values[topic.topicCode] / 100).toFixed(1)}%`
                                : metric.values[topic.topicCode].toFixed(1)
                              : 'N/A'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
